import {
  Grid,
  Select,
  MenuItem,
  TextField,
  InputLabel,
  FormControl,
} from '@mui/material';

import { useTranslate } from 'src/locales';
import { ActivationTerms } from 'src/services/admin/admin.types';
import { useGetProductsQuery } from 'src/services/billing/billing.service';

import QuantityInput from 'src/components/number-input/quantity-input';

import { TenantType } from 'src/types/enums';
import { PayPeriod } from 'src/types/payment';

type Props = {
  tenantType?: TenantType;
  value: ActivationTerms;
  onChange: (terms: ActivationTerms) => void;
  disabled?: boolean;
};

/**
 * Plan, billing period, seat count and Stripe promotion code.
 *
 * Shared by the activation card on an existing pending tenant and by the create-tenant dialog, so
 * a super admin sets terms the same way whichever direction the tenant came from.
 *
 * The promo code is free text: it is looked up in Stripe when the link is sent, and the request is
 * rejected with `InvalidPromoCode` if it does not exist there.
 */
export default function ActivationTermsFields({ tenantType, value, onChange, disabled }: Props) {
  const { t } = useTranslate();

  const { data: products } = useGetProductsQuery();

  const availablePlans = (products ?? []).filter(
    (product) =>
      product.subscription_required &&
      (!tenantType || product.tenant_type === tenantType)
  );

  return (
    <>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth size="small">
          <InputLabel id="activation-plan-label">
            {t('restricted.organizations.detail.activation.plan')}
          </InputLabel>
          <Select
            labelId="activation-plan-label"
            label={t('restricted.organizations.detail.activation.plan')}
            value={value.product_identifier}
            disabled={disabled}
            onChange={(e) => onChange({ ...value, product_identifier: e.target.value })}
            inputProps={{ 'data-testid': 'activation-plan-select' }}
          >
            {availablePlans.map((plan) => (
              <MenuItem key={plan.identifier} value={plan.identifier}>
                {plan.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth size="small">
          <InputLabel id="activation-period-label">
            {t('restricted.organizations.detail.activation.billing_period')}
          </InputLabel>
          <Select
            labelId="activation-period-label"
            label={t('restricted.organizations.detail.activation.billing_period')}
            value={value.billing_period}
            disabled={disabled}
            onChange={(e) => onChange({ ...value, billing_period: e.target.value })}
          >
            <MenuItem value={PayPeriod.Monthly}>{t('common.monthly')}</MenuItem>
            <MenuItem value={PayPeriod.Yearly}>{t('common.yearly')}</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl size="small">
          <InputLabel shrink>
            {t('restricted.organizations.detail.activation.seats')}
          </InputLabel>
          <QuantityInput
            min={1}
            value={value.seats}
            onChange={(_e, val) => onChange({ ...value, seats: (val as number) ?? 1 })}
          />
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size="small"
          label={t('restricted.organizations.detail.activation.promo_code')}
          helperText={t('restricted.organizations.detail.activation.promo_code_helper')}
          value={value.promo_code ?? ''}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, promo_code: e.target.value })}
          inputProps={{ 'data-testid': 'activation-promo-code-input' }}
        />
      </Grid>
    </>
  );
}
